import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';

import { ArmyBuilderConfig } from '../config';
import { entitySyncComplete, entitySyncError, SelectSyncStatus, startEntitySync } from '../data';
import { HttpClientWithInFlightCache } from '../httpClient';

export interface UserDataSyncStatus {
    status: 'SYNCING' | 'IDLE' | 'ERROR';
    message?: string;
}

export const SYNC_DEBOUNCE = 10000;

@Injectable({ providedIn: 'root' })
export abstract class BaseSyncService {
    abstract entityType: string;

    // Common sync status observable for all sync services
    syncStatus$ = new BehaviorSubject<UserDataSyncStatus>({ status: 'IDLE' });
    firstSyncComplete = false;
    MIN_LOADING_DELAY = 2000;

    // Track when each entity started syncing to ensure minimum display time
    protected syncStartTimes: Map<string, number> = new Map();
    protected readonly MIN_SYNC_DISPLAY_MS = 400;
    protected readonly MAX_SYNC_DISPLAY_MS = 600;

    constructor(
        protected httpClient: HttpClientWithInFlightCache,
        protected store: Store<any>,
        protected modalController: ModalController,
        protected config: ArmyBuilderConfig
    ) {
        this.init();
    }

    abstract init();
    abstract sync(options?: { manual?: boolean }): Promise<void> | void;

    protected getSyncKey(entityType: string, entityId?: string): string {
        return entityId ? `${entityType}:${entityId}` : entityType;
    }

    protected getRandomDelay(): number {
        return Math.floor(Math.random() * (this.MAX_SYNC_DISPLAY_MS - this.MIN_SYNC_DISPLAY_MS + 1)) + this.MIN_SYNC_DISPLAY_MS;
    }

    protected start(entityType: string, entityId?: string) {
        const key = this.getSyncKey(entityType, entityId);
        this.syncStartTimes.set(key, Date.now());
        this.store.dispatch(startEntitySync({ entityType, entityId }));
    }

    protected error(entityType: string, entityId?: string) {
        const key = this.getSyncKey(entityType, entityId);
        const startTime = this.syncStartTimes.get(key) || 0;
        const elapsed = Date.now() - startTime;
        const minDelay = this.getRandomDelay();
        const remainingDelay = Math.max(0, minDelay - elapsed);

        setTimeout(() => {
            this.syncStartTimes.delete(key);
            this.store.dispatch(entitySyncError({ entityType, entityId }));
            // Update global sync status to ERROR so sync status bar disappears
            this.syncStatus$.next({ status: 'ERROR' });
        }, remainingDelay);
    }

    protected complete(entityType: string, entityId?: string) {
        const key = this.getSyncKey(entityType, entityId);
        const startTime = this.syncStartTimes.get(key) || 0;
        const elapsed = Date.now() - startTime;
        const minDelay = this.getRandomDelay();
        const remainingDelay = Math.max(0, minDelay - elapsed);

        setTimeout(() => {
            this.syncStartTimes.delete(key);
            this.store.dispatch(entitySyncComplete({ entityType, entityId }));
            this.refreshSyncStatus();
        }, remainingDelay);
    }

    protected refreshSyncStatus() {
        // Always set back to IDLE - dirty entities will trigger their own sync via subscriptions
        this.syncStatus$.next({ status: 'IDLE', message: null });
    }

    protected getSyncUrl(id?: string) {
        return `${this.config.apiBaseUrl}/userData/${this.entityType}${id ? '/' + id : ''}`;
    }

    protected getServerTimestamp(entityId?: string): Observable<number> {
        const headers = this.config.globalRequestHeaders;
        return this.httpClient.get(`${this.getSyncUrl(entityId)}/timestamp`, {
            headers,
            withCredentials: true,
            requiresLogin: true
        });
    }

    protected getServerTimestamps(): Observable<{ [key: string]: number }> {
        const headers = this.config.globalRequestHeaders;
        const syncUrl = this.getSyncUrl();
        return this.httpClient.get(`${syncUrl}/timestamp`, {
            headers,
            withCredentials: true,
            requiresLogin: true
        });
    }

    protected getStatus(entityType: string, entityId?: any) {
        return this.store.select(SelectSyncStatus, { entityType: entityType, id: entityId });
    }

    protected handleSyncError(entityId?: string) {
        return catchError((err) => {
            this.error(this.entityType, entityId);
            throw err;
        });
    }
}
